import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { AppContext } from '../../../App';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import AddCircleIcon from '@mui/icons-material/AddCircle';

import { useCreateTrainingSet } from '../../hooks/useCreateTrainingSet';
import ButtonGlynt from '../../common/Buttons/ButtonGlynt';
import ValidTextFieldTS from '../../train/ValidTextFieldTS';

interface ICreateTrainingSetProps {
  documentId: string;
}

function CreateTrainingSet({ documentId }: ICreateTrainingSetProps) {
  const { setToast, dataPoolId } = React.useContext(AppContext);

  const [disabled, setDisabled] = React.useState(true);
  const [open, setOpen] = React.useState(false);

  const [trainingSetLabel, setTrainingSetLabel] = useState<string>('');

  const queryClient = useQueryClient();

  const { mutateAsync: createTrainingSet, isPending } = useCreateTrainingSet({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSetsByDocument']
      });
      queryClient.invalidateQueries({
        queryKey: ['documents']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Training Set Created!',
      });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      let message = error.message || 'Sorry, failed to create training set.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
    },
  });

  const closeModal = () => {
    setOpen(false);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <ButtonGlynt
          variant="outlined"
          size="xs"
          data-cy="btn-create-training-set"
          onClick={() => {
            setOpen(true);
          }}
          sx={{ marginTop: '5px' }}
        >
          <AddCircleIcon sx={{ fontSize: 'small' }} />
          &nbsp;Create
        </ButtonGlynt>
      </div>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        style={{ display: `${open ? '' : 'none'}` }}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') {
            closeModal();
          }
        }}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{`Create New Training Set With Document`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid container justifyContent="center" alignItems="center" item xs={12}>
              <ValidTextFieldTS
                placeholder="Training Set Name"
                cypressId="input-new-training-set-name"
                setLabel={setTrainingSetLabel}
                disabled={false}
                setDisabled={setDisabled}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            id="training-set-add-dialog-cancel"
            data-cy="training-set-add-dialog-cancel"
            onClick={() => {
              closeModal();
            }}
            color="primary"
          >
            Cancel
          </Button>

          <Button
            id="button-add-to-training-set-submit"
            color="primary"
            data-cy="button-add-to-training-set-submit"
            onClick={async () => {
              if (!trainingSetLabel) {
                setToast({
                  open: true,
                  variant: 'error',
                  message: 'Unable to get training set data.',
                });
                return;
              }

              const data = await createTrainingSet({
                label: trainingSetLabel,
                documents: [documentId],
              });
            }}
            disabled={isPending || disabled}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CreateTrainingSet;
